import React, { useEffect } from 'react';
import { Box, Static, Text, useStdout } from 'ink';
import { InputComponent } from './MessageInput';
import type {
  AppState,
  AppCallbacks,
  ChatElement,
  AgentMode,
  Message,
  RetryStatus,
  AgentTreeView,
} from './types';
import { ConnectionState } from './types';
import type { DropdownProvider } from './lib/dropdown_provider';
import { DEFAULT_TERMINAL_WIDTH, CLI_INPUT_TYPES, ONBOARDING_CARD_ENABLED } from './constants';
import { LoadingIndicator } from './LoadingIndicator';
import { useInputAction, usePublishInputContext } from './lib/keymap';
import { useStaticReset } from './lib/hooks/use_static_reset';
import { computeStaticElements } from './lib/hooks/compute_static_elements';
import { useCancelStream, CANCEL_HINT, type CancelState } from './lib/hooks/use_cancel_stream';
import { useTransientFlag } from './lib/hooks/use_transient_flag';
import { InteractiveModeHeader } from './lib/components/InteractiveModeHeader';
import { StatusBar } from './lib/components/StatusBar';
import { McpStatusIndicator } from './lib/components/McpStatusIndicator';
import { ContextUsageIndicator } from './lib/components/ContextUsageIndicator';
import { RetryStatusIndicator } from './lib/components/RetryStatusIndicator';
import { UpdateBanner } from './lib/components/UpdateBanner';
import { DeprecationBanner, useDeprecationStatus } from './lib/components/DeprecationBanner';
import { OnboardingCard, onboardingCardVisibleForInput } from './lib/components/OnboardingCard';
import { WelcomeCard } from './lib/components/WelcomeCard';
import { ChatMessage } from './ChatMessage';
import { ControlsHint } from './lib/components/ControlsHint';
import { SubSessionPanel } from './lib/components/SubSessionPanel';
import { SubsessionChordPanel, useSubsessionChord } from './lib/components/SubsessionChordPanel';
import { resolveSubSession } from './lib/sub_session';
import { useCommandComponentRegistry } from './lib/command_component_registry';
import { resolveFooterHint } from './lib/footer_hint';
import { getAgentColor, getAgentPrefix } from './lib/colors';
import { useTheme } from './lib/theme';
import { SubAgentIndicator, hasRunningSubAgent } from './lib/components/SubAgentIndicator';
import { ProjectIndicator } from './lib/components/ProjectIndicator';

const ESC = '\x1b';
const BEL = '\x07';
const MAX_TITLE_LENGTH = 60;

type SafeTitle = string & { readonly __brand: 'SafeTitle' };

export const truncateTitle = (content: string): SafeTitle => {
  const line = content.split('\n')[0].replaceAll(ESC, '').replaceAll(BEL, '');
  return (
    line.length > MAX_TITLE_LENGTH ? `${line.slice(0, MAX_TITLE_LENGTH - 1)}…` : line
  ) as SafeTitle;
};

export { getAgentColor };

interface AgentModeIndicatorProps {
  availableAgents: AgentMode[];
  selectedAgent: AgentMode;
}

const AgentModeIndicator: React.FC<AgentModeIndicatorProps> = ({
  availableAgents,
  selectedAgent,
}) => {
  const { tokens } = useTheme();
  if (availableAgents.length <= 1) return null;

  return (
    <Box>
      <Text color={getAgentColor(tokens, selectedAgent)} bold>
        {selectedAgent}
      </Text>
      <ControlsHint>{` (**tab** to switch)`}</ControlsHint>
    </Box>
  );
};

interface PromptModeIndicatorProps {
  inputType: string;
  footerHint: string | null;
  availableAgents: AgentMode[];
  selectedAgent: AgentMode;
  inSubSession: boolean;
}

/**
 * Displays the build/plan mode switcher,
 * or the active menu's navigation hint when a dropdown is open
 * inside the prompt box footer.
 */
const PromptModeIndicator: React.FC<PromptModeIndicatorProps> = ({
  inputType,
  footerHint,
  availableAgents,
  selectedAgent,
  inSubSession,
}) => {
  if (inputType === CLI_INPUT_TYPES.CHOICE) {
    return null;
  }

  if (footerHint) {
    return <Text dimColor>{footerHint}</Text>;
  }

  if (inSubSession) {
    return null;
  }

  return <AgentModeIndicator availableAgents={availableAgents} selectedAgent={selectedAgent} />;
};

interface GlobalStatusHintProps {
  inputType: string;
  isLoading: boolean;
  cancelState: CancelState;
  retryStatus?: RetryStatus;
  hasQueuedPrompt: boolean;
  inSubSession: boolean;
}

/**
 * - Tool-approval hints live inside ChoiceInput.
 * - Slash-command dialog hints are rendered directly below their dialog component.
 */
const GlobalStatusHint: React.FC<GlobalStatusHintProps> = ({
  inputType,
  isLoading,
  cancelState,
  retryStatus,
  hasQueuedPrompt,
  inSubSession,
}) => {
  if (inputType !== CLI_INPUT_TYPES.TEXT) {
    return null;
  }

  if (retryStatus) {
    return <RetryStatusIndicator retryStatus={retryStatus} />;
  }

  if (isLoading && !hasQueuedPrompt && !inSubSession) {
    return <ControlsHint>{CANCEL_HINT[cancelState]}</ControlsHint>;
  }

  return null;
};

const SubAgentIndicatorWithDot: React.FC<{ agentTree?: AgentTreeView }> = ({ agentTree }) => {
  if (!agentTree) return null;
  if (!hasRunningSubAgent(agentTree)) return null;

  return (
    <Box>
      <Text dimColor> · </Text>
      <SubAgentIndicator agentTree={agentTree} dimColor />
    </Box>
  );
};

interface QueuedPromptIndicatorProps {
  queuedPrompt: string;
  // Esc only clears the queue in text mode; the hint would mislead otherwise
  // (e.g. while a tool-approval choice owns the input).
  canCancel: boolean;
}

const QueuedPromptIndicator: React.FC<QueuedPromptIndicatorProps> = ({
  queuedPrompt,
  canCancel,
}) => {
  // Strip ESC/BEL so a pasted control sequence cannot escape into the rendered row.
  const lines = queuedPrompt.split('\n');
  const preview = lines[0].replaceAll(ESC, '').replaceAll(BEL, '').trim();
  const moreLines = lines.length > 1 ? ` +${lines.length - 1}` : '';

  return (
    <Box>
      <Text dimColor>↳ Up next: </Text>
      <Box flexGrow={1}>
        <Text dimColor wrap="truncate">
          {preview}
          {moreLines}
        </Text>
      </Box>
      {canCancel && <Text dimColor> · Esc to cancel</Text>}
    </Box>
  );
};

interface ChatInterfaceProps {
  state: AppState;
  callbacks: AppCallbacks;
  dropdownProviders?: DropdownProvider[];
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  state,
  callbacks,
  dropdownProviders,
}) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [inputEmpty, setInputEmpty] = React.useState(true);
  const commandRegistry = useCommandComponentRegistry();
  const { name: themeName, tokens } = useTheme();

  const subSession = resolveSubSession(state.agentTree);
  const activeNode = subSession?.node ?? state.agentTree?.root;
  const showEscHint = useTransientFlag();
  const subagents = activeNode?.children ?? [];
  const subsessionChord = useSubsessionChord(subagents.length, callbacks.onEnterSubsession);

  // Publish app-level facts into the central keymap context so the dispatcher
  // can arbitrate the conflict-prone keys deterministically.
  usePublishInputContext({
    inputType: state.input.inputType,
    isLoading: state.isLoading,
    hasQueuedPrompt: state.queuedPrompt !== undefined,
    inSubSession: subSession !== undefined,
    viewedNodeHasChildren: subagents.length > 0,
  });

  // Global shortcuts owned by the centralized keymap dispatcher.
  useInputAction('expand.toggle', callbacks.toggleExpanded);
  useInputAction('history.open', callbacks.onOpenHistorySearch);
  useInputAction('agent.cycle', callbacks.onCycleAgent);
  useInputAction('queue.cancel', callbacks.onCancelQueuedPrompt);
  useInputAction('app.exit', callbacks.onExit);
  useInputAction('subsession.exit', callbacks.onExitSubsession);
  useInputAction('subsession.escHint', showEscHint.trigger);

  const cancelState = useCancelStream(
    state.isLoading,
    state.queuedPrompt !== undefined,
    callbacks.onCancelStream,
  );
  const footerHint = resolveFooterHint(state.input, commandRegistry, dropdownOpen);

  // Read columns here (outside <Static>) so the value is stable during live→static transitions.
  // useStdout is unavailable inside <Static>, so components rendered there must receive
  // columns as a prop rather than calling useStdout themselves.
  const { stdout } = useStdout();
  const columns = stdout?.columns ?? DEFAULT_TERMINAL_WIDTH;
  const rows = stdout?.rows ?? 24;

  const deprecationStatus = useDeprecationStatus();
  const allDataInitialized = Boolean(
    state.username &&
      state.gitlabRemoteInfo.status !== 'not-checked' &&
      state.updateCheckResult &&
      deprecationStatus !== undefined,
  );

  const headerContent = (
    <Box key="header" marginBottom={1}>
      <InteractiveModeHeader
        username={state.username}
        credentialSource={state.credentialSource}
        gitlabRemoteInfo={state.gitlabRemoteInfo}
        cwd={state.cwd}
        agenticChatAccess={state.agenticChatAccess}
        columns={columns}
      />
    </Box>
  );

  const updateBannerContent = state.updateCheckResult?.type === 'needs-update' && (
    <UpdateBanner
      key="update-banner"
      updateInfo={state.updateCheckResult.updateInfo}
      columns={columns}
    />
  );
  const deprecationBannerContent = deprecationStatus && deprecationStatus !== 'not-applicable' && (
    <DeprecationBanner key="deprecation-banner" status={deprecationStatus} columns={columns} />
  );
  const isEmpty = state.elements.length === 0;
  const isTextInput = state.input.inputType === CLI_INPUT_TYPES.TEXT;
  const isChoiceInput = state.input.inputType === CLI_INPUT_TYPES.CHOICE;
  const choiceApproval =
    state.input.inputType === CLI_INPUT_TYPES.CHOICE ? state.input.approval : undefined;
  const subagentApprovalPending = choiceApproval?.requestedBy !== undefined;
  // The requesting agent's own view already shows the pending card in its log;
  // embedding it in the prompt as well would render it twice.
  const approvalCardInView =
    choiceApproval !== undefined &&
    state.elements.some((element) => element.id === choiceApproval.toolCall.id);
  const inputForRender =
    state.input.inputType === CLI_INPUT_TYPES.CHOICE && approvalCardInView
      ? { ...state.input, approval: undefined }
      : state.input;
  // The onboarding card stays visible while the plain text prompt is active and,
  // as a special case, while the MCP panel is open — MCP is one of the onboarding
  // steps, so keeping the checklist in view reinforces that context. Every other
  // slash-command panel/dialog (/help, /model, …) hides it. Feature-flagged off
  // for now; when hidden it must not claim the input's ↑/↓ (see `onboardingActive`).
  const showOnboarding =
    isEmpty &&
    !subSession &&
    ONBOARDING_CARD_ENABLED &&
    onboardingCardVisibleForInput(state.input.inputType);
  // The welcome card occupies the same empty-screen slot as the onboarding card
  // and claims the same keys, so the two are mutually exclusive — onboarding
  // wins when its flag is on. Shown only on the plain text prompt, and only
  // once the caller resolved the "Show work items in new sessions" setting to
  // enabled — `undefined` (still resolving) keeps the card hidden so a
  // disabled card never flashes its loader during startup.
  const showWelcome =
    isEmpty && !subSession && !showOnboarding && isTextInput && state.welcomeCardEnabled === true;

  const mcpConnectedCount =
    state.mcpServers?.filter((s) => s.connectionState === ConnectionState.Connected).length ?? 0;

  const onboardingStepsCompletionMap = {
    ...state.stepsCompletionMap,
    mcp: state.mcpServers === undefined ? ('pending' as const) : mcpConnectedCount > 0,
  };

  // From the root log, so the terminal-tab title survives entering a sub-session.
  const rootElements = state.agentTree?.root.elements ?? state.elements;
  const firstUserMessage = rootElements.find(
    (el): el is Message => el.type === 'message' && el.role === 'user',
  );
  const sessionTitle = firstUserMessage ? truncateTitle(firstUserMessage.content) : undefined;

  useEffect(() => {
    if (sessionTitle && process.stderr?.isTTY) {
      process.stderr.write(`${ESC}]0;${sessionTitle}${BEL}`);
      return () => {
        process.stderr.write(`${ESC}]0;${BEL}`);
      };
    }
    return undefined;
  }, [sessionTitle]);

  // Keyed on the viewed node and the resolved theme too: <Static> never un-emits, so
  // switching nodes or restyling frozen content needs a reset (clear + replay).
  // Main view keys on the session alone — no reset when the first delegation appears.
  const staticView =
    state.sessionId && `${state.sessionId}:${subSession?.node.id ?? ''}:${themeName}`;
  const staticNonce = useStaticReset(staticView || undefined);
  const { useStaticOptimization, frozenElements, liveElements } = computeStaticElements(
    state.elements,
    allDataInitialized,
  );

  // Each element owns its own spacing via ChatMessage — no margin logic needed here.
  const renderElement = (element: ChatElement) => (
    <ChatMessage
      key={element.id}
      element={element}
      expanded={state.expanded}
      columns={columns}
      rows={rows}
    />
  );

  return (
    <Box flexDirection="column" height="100%">
      {/* No bottom margin during tool approval — ChoiceInput sits directly below the tool
          card with no gap, matching the visual weight of the options list. For all other
          input types a blank row separates the chat content from the input box. */}
      <Box
        flexDirection="column"
        flexGrow={1}
        marginBottom={state.input.inputType === CLI_INPUT_TYPES.CHOICE ? 0 : 1}
      >
        {useStaticOptimization ? (
          <>
            {/* Use Static for header, update banner, and frozen elements. */}
            {/* eslint-disable-next-line no-restricted-syntax */}
            <Static
              key={staticNonce}
              items={[
                headerContent,
                ...(deprecationBannerContent ? [deprecationBannerContent] : []),
                ...(updateBannerContent ? [updateBannerContent] : []),
                ...frozenElements,
              ]}
            >
              {(item) => {
                if (React.isValidElement(item)) {
                  return item;
                }
                return renderElement(item as ChatElement);
              }}
            </Static>
            {!isEmpty && <Box flexDirection="column">{liveElements.map(renderElement)}</Box>}
          </>
        ) : (
          <>
            {headerContent}
            {deprecationBannerContent}
            {updateBannerContent}
            {!isEmpty && state.elements.map(renderElement)}
          </>
        )}
        {isEmpty && subSession && (
          <Text dimColor>
            No log entries from this sub-agent yet — its flow may not report progress.
          </Text>
        )}
        {isEmpty && (
          <>
            {showOnboarding && (
              <OnboardingCard
                onRun={callbacks.onSubmit}
                active={inputEmpty && isTextInput}
                stepsCompletionMap={onboardingStepsCompletionMap}
                mcpConnectedCount={mcpConnectedCount}
              />
            )}
            {showWelcome && (
              <WelcomeCard
                onRun={callbacks.onSubmit}
                active={inputEmpty && isTextInput}
                categories={state.welcomePrompts}
              />
            )}
          </>
        )}
      </Box>

      {state.isLoading && <LoadingIndicator />}
      {state.queuedPrompt !== undefined && (
        <QueuedPromptIndicator
          queuedPrompt={state.queuedPrompt.prompt}
          canCancel={isTextInput && !subSession}
        />
      )}
      {subsessionChord.active ? (
        // The armed chord owns the prompt area when active. Whatever was there
        // comes back untouched when the chord clears,
        // since state.input is never modified.
        <SubsessionChordPanel
          subagents={subagents}
          selectedChildNumber={subsessionChord.selectedChildNumber}
          columns={columns}
        />
      ) : (
        <>
          {!(subSession && isTextInput) && (
            <InputComponent
              input={inputForRender}
              callbacks={callbacks}
              dropdownProviders={dropdownProviders}
              onDropdownOpenChange={setDropdownOpen}
              onMessageEmptyChange={setInputEmpty}
              title={firstUserMessage ? sessionTitle : undefined}
              borderColor={getAgentColor(tokens, state.selectedAgent)}
              promptPrefix={getAgentPrefix(state.selectedAgent)}
              onboardingActive={showOnboarding}
              // TODO: when all steps complete the card stays visible but navigableIds becomes empty,
              // so TextInput cedes ↑/↓ while nobody claims them and the footer still shows "↑↓ to choose".
              // Fix: key TextInput off the published onboardingActive context instead of showOnboarding,
              // and hide the footer hint when navigableIds is empty.
              welcomeActive={showWelcome}
              subagentApprovalPending={subagentApprovalPending}
              footer={
                isTextInput ? (
                  <Box width="100%">
                    <PromptModeIndicator
                      inputType={state.input.inputType}
                      footerHint={footerHint}
                      availableAgents={state.availableAgents}
                      selectedAgent={state.selectedAgent}
                      inSubSession={subSession !== undefined}
                    />
                    <Box flexGrow={1} justifyContent="flex-end" gap={2}>
                      <ContextUsageIndicator contextUsage={state.contextUsage} />
                      {state.selectedModel && <Text dimColor>{state.selectedModel}</Text>}
                    </Box>
                  </Box>
                ) : undefined
              }
            />
          )}
          {isChoiceInput && (
            <Box paddingLeft={choiceApproval && !approvalCardInView ? 4 : 2} marginBottom={1}>
              <Text dimColor>
                {subagentApprovalPending
                  ? '↑↓ to choose · enter to confirm · the sub agent is blocked until you answer'
                  : `Use ↑/↓ arrows to navigate • Enter to select • Ctrl+C to exit${
                      state.agentTree !== undefined ? ' • ←/Ctrl+T to switch agent view' : ''
                    }`}
              </Text>
            </Box>
          )}
          {!isTextInput && !isChoiceInput && footerHint && <Text dimColor>{footerHint}</Text>}
        </>
      )}
      {/* A divider is shown above the bar when there is no bordered input box
          immediately above (i.e. anything other than the text input). */}
      <Box flexDirection="column">
        {!isTextInput && subSession === undefined && !subsessionChord.active && (
          <Box
            borderBottom={false}
            borderLeft={false}
            borderRight={false}
            borderStyle="single"
            borderColor={tokens.text.subtle}
          />
        )}
        {subSession && (isTextInput || isChoiceInput) && (
          <SubSessionPanel subSession={subSession} showEscHint={showEscHint.active} />
        )}
        <StatusBar>
          <Box gap={2}>
            <Box>
              <ProjectIndicator gitlabRemoteInfo={state.gitlabRemoteInfo} cwd={state.cwd} />
              {!subSession && <SubAgentIndicatorWithDot agentTree={state.agentTree} />}
            </Box>
            <GlobalStatusHint
              inputType={state.input.inputType}
              isLoading={state.isLoading}
              cancelState={cancelState}
              retryStatus={state.retryStatus}
              hasQueuedPrompt={state.queuedPrompt !== undefined}
              inSubSession={subSession !== undefined}
            />
          </Box>
          <McpStatusIndicator mcpServers={state.mcpServers} />
        </StatusBar>
      </Box>
    </Box>
  );
};
